import {type FunctionalComponent, h} from '@stencil/core';
import {DashboardPrivateTile} from '../../dashboard-private/dashboard-private-tile';
import { DashboardDocumentsTile } from '../../dashbord-documents/dashboard-documents-tile';
import { DashboardCompanyTile } from '../../dashboard-company-tile/dashboard-company-tile';
import {DashboardProducts} from '../../dashboard-products/dashboard-products';
import type {SearchProduct} from '../../../../../../../api/model/response/documents/public/Product';
import {MySeDashboardWidgetsMiddle} from '../../dashboard-widgets/myse-dashboard-widgets-middle';
import {DashboardItemHeader} from '../../dashboard-item-header/dashboard-item-header';
import type {IDashboardDesktopView} from './myse-dashboard-desktop-view';
import {useLimitedWidgetDocs} from './helpers';
import { CtaWidgetsRow } from '../../dashboard-widgets/cta-widgets-row';

export const MyseDashboardDesignFirm: FunctionalComponent<Partial<IDashboardDesktopView>> = (props) => {
    const {
      isSupport,
      isOffsitesVisible,
      labelProvider,
      searcherNames,
      getTabCount,
      strategies,
      queryProp,
      rtl,
      reflectedTabs,
      resultRenderer,
      faqData,
      brand,
      country,
      language,
      resultClickAnalytics,
      sectionsData,
      relatedProductName,
      tabName,
      isSubstitutionTool,
      eolDiscontinuedResults,
      isUnifiedCheckout
    } = props;

    const [documentsData] = useLimitedWidgetDocs(['documents'], sectionsData);
    const [trainingsData, softwareData, topicsData] = useLimitedWidgetDocs(['trainings','software', 'topics'], sectionsData);
    const [solutionsData] = useLimitedWidgetDocs(['solutions'], sectionsData);
    const [communityData, videoData] = useLimitedWidgetDocs(['support/community', 'support/video'], sectionsData);

    const renderDashboardPrivateTile = (doc, titleKey, titleDefault, targetTab, section, privateCard = false, customValueClass = '') => (
      <DashboardPrivateTile
        doc={doc}
        labelProvider={(e, f) => labelProvider(e, f)}
        title={labelProvider(titleKey, titleDefault)}
        targetTab={targetTab}
        tabCount={getTabCount(targetTab)}
        strategies={strategies}
        section={section}
        privateCard={privateCard}
        limitResults={doc.length < 3}
        term={queryProp}
        rtl={rtl}
        customValueClass={customValueClass}

      />
    );

    const renderDashboardProducts = () => (
      <DashboardProducts
        tabName={reflectedTabs?.[searcherNames?.products]?.tab}
        strategies={strategies}
        resultRenderer={resultRenderer}
        resultClickAnalytics={resultClickAnalytics}
        products={sectionsData.products as SearchProduct[]}
        tabCount={reflectedTabs?.[searcherNames?.products]?.count}
        labelProvider={(e, f) => labelProvider(e, f)}
        country={country}
        language={language}
        isEcommerceMode={false}
        eShop={false}
        rtl={rtl}
        targetTab={searcherNames.products}
        brand={brand}
        subTitle={true}
        query={queryProp}
        isSubstitutionTool={isSubstitutionTool}
        eolDiscontinuedResults={eolDiscontinuedResults}
        isProductData={sectionsData?.products?.length > 0}
        isUnifiedCheckout={isUnifiedCheckout}
      />
    );

    const renderCtaWidgetsRow = () => (
      <CtaWidgetsRow
        labelProvider={labelProvider}
        country={country}
        language={language}
        strategies={strategies}
      />
    );

    const renderFaqWidget = () => (
      <uiaas-search-widget-faq
        country={country}
        language={language}
        rtl={rtl}
        maxItems={3}
        tab-name={tabName}
        termAttribute={queryProp}
        tabName={searcherNames.all}
        tabCount={getTabCount(searcherNames.faq)}
        targetTab={searcherNames.faq}
        dashboardSearcherData={faqData}
        isDashboardView={true}
        labelProvider={(e, f) => labelProvider(e, f)}
      >
        <div slot="dashboard-section-header">
          <DashboardItemHeader
            itemCount={faqData.length}
            labelProvider={(e, f) => labelProvider(e, f)}
            tabCount={getTabCount(searcherNames.faq)}
            targetTab={searcherNames.faq}
            title=""
            term={props.queryProp}
            subTitle={true}
          />
        </div>
      </uiaas-search-widget-faq>
    );

    const renderDashboardWidgetsMiddle = () => (
      <MySeDashboardWidgetsMiddle
        labelProvider={labelProvider}
        hasCtaProductSelector={!!sectionsData?.products?.length}
        country={country}
        language={language}
        term={queryProp}
        analyticsTabName={'all'}
        brand={brand}
        tabName={tabName}
        isOffsitesWidget={isOffsitesVisible}
        relatedProductName={relatedProductName}
        strategies={strategies}
      />
    );

    const renderSupportView = () => [
      <div class="dashboard__tiles">
        {renderDashboardPrivateTile(documentsData, 'tab.name.documents', 'Documents', searcherNames.documents, 'documents')}
        {renderDashboardPrivateTile(softwareData, 'tab.name.software', 'Software', searcherNames.software, 'software')}
      </div>,
      renderDashboardProducts(),
      <div class={{ 'support-faq-widget dashboard--full-width': faqData.length > 0 }}>
        {renderFaqWidget()}
      </div>,
      <div class={{ 'dashboard__tiles': isOffsitesVisible, 'widget-offset-bottom': !isOffsitesVisible }}>
        {renderDashboardWidgetsMiddle()}
      </div>,
      <div class="dashboard__tiles">
        <DashboardDocumentsTile
          doc={communityData}
          labelProvider={(e, f) => labelProvider(e, f)}
          title={labelProvider('tab.name.community', 'Community')}
          targetTab={searcherNames['support/community']}
          tabCount={getTabCount(searcherNames['support/community'])}
          strategies={strategies}
          section="support/community"
          limitResults={communityData?.length > 0}
          query={queryProp}
          rtl={rtl}
          country={country}
          language={language}
        />
        <DashboardCompanyTile
          doc={videoData}
          labelProvider={(e, f) => labelProvider(e, f)}
          title={labelProvider('tab.name.video', 'Video')}
          targetTab={searcherNames['support/video']}
          tabCount={getTabCount(searcherNames['support/video'])}
          strategies={strategies}
          section="support/video"
          limitResults={videoData?.length > 0}
          query={queryProp}
          rtl={rtl}
          country={country}
          language={language}
        />
      </div>,
    ];

    const renderDefaultView = () => [
      <div class="dashboard__tiles">
        {renderDashboardPrivateTile(documentsData, 'tab.name.documents', 'Documents', searcherNames.documents, 'documents')}
      </div>,
      <div class="dashboard__tiles dashboard__tiles--gapped">
        {renderDashboardPrivateTile(trainingsData, 'tab.name.trainings', 'Trainings', searcherNames.trainings, 'trainings', true, 'tail__container--no-margin')}
        {renderDashboardPrivateTile(softwareData, 'tab.name.software', 'Software', searcherNames.software, 'software', false, 'tail__container--no-margin')}
        {renderDashboardPrivateTile(topicsData, 'tab.name.topics', 'Topics for you', searcherNames.topics, 'topics', true, 'tail__container--no-margin')}
      </div>,
      <div class="dashboard__tiles">
        {renderDashboardPrivateTile(solutionsData, 'tab.name.solutions', 'Solutions', searcherNames.solutions, 'solutions')}
      </div>,
      renderDashboardProducts(),
      <div class={{ 'support-faq-widget dashboard--full-width': faqData.length > 0 }}>
        {renderFaqWidget()}
      </div>,
      <div class="dashboard__tiles dashboard__tiles--myse widget-offset-bottom-myse">
        {renderCtaWidgetsRow()}
      </div>,
      <div class={{ 'dashboard__tiles': isOffsitesVisible, 'widget-offset-bottom': !isOffsitesVisible }}>
        {renderDashboardWidgetsMiddle()}
      </div>
    ];

    return isSupport ? renderSupportView() : renderDefaultView();
};
